import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatInput, ChatMessage, ChatSuggestions } from './ChatComponents';
import type { AnswerDepth } from './AnswerDepth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import {
  primitiveColors,
  semanticColors,
  BORDER_RADIUS,
  MAX_WIDTH_XS,
  MAX_WIDTH_SM,
  MAX_WIDTH_MD,
  MAX_WIDTH_LG,
} from '@/theme/tokens';
import { ChatBubble } from './ChatBubble/ChatBubble';
import { ChatLandingState } from './ChatLandingState';
import { useBallotSheet } from '@/providers/BallotSheetProvider';
import BallotGrabber from '@/components/ballot/BallotGrabber';
import { useNavMode } from '@/hooks/useNavMode';
import { useAuth } from '@/components/auth/AuthProvider';
import useGuestAddress from '@/hooks/useGuestAddress';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import { readGuestChat } from '@/utils/guestChatStorage';

/** Set when the user taps "See my ballot" on the guest landing page.
 * Consumed once on /chat mount to snap the BallotSheet to the ``full``
 * detent (sheet mode) or open the Ballot tab (tab mode), then cleared. */
export const LANDING_OPEN_FULL_FLAG = 'votemate.landing.openFull';
/** Set when the user taps "Skip for now — just chat" on the guest landing
 * page. Consumed once on /chat mount: a guest with no address otherwise opens
 * on the mobile Ballot tab, and this arrival asked for the chat instead. */
export const LANDING_SKIPPED_FLAG = 'votemate.landing.skipped';

interface ChatWindowProps {
  messages: ChatMessage[];
  suggestions: string[];
  onSend: (value: string) => void;
  onSuggestionClick: (suggestion: string) => void;
  disabled?: boolean;
  isHistoricalConversation?: boolean;
  onStartNewConversation?: () => void;
  onFeedbackSubmit?: (
    messageId: string,
    feedbackType: 'thumbs_up' | 'thumbs_down',
    feedback: string
  ) => void;
  feedbackConfirmations?: { [messageId: string]: 'thumbs_up' | 'thumbs_down' };
  conversationId?: string | null;
  userId?: string;
  onMenuTrigger?: () => void;
  streamState?: {
    event: 'start' | 'message' | 'memory_updated' | 'new_conversation' | 'end';
    loading: boolean;
    loadingMessage?: string;
  };
  isBallotOpen: boolean;
  userName?: string;
  isReturningUser?: boolean;
  /** Answer depth for this session, threaded down to the input's selector. */
  depth?: AnswerDepth;
  onDepthChange?: (depth: AnswerDepth) => void;
}

export const ChatWindow = React.memo<ChatWindowProps>(function ChatWindow({
  messages,
  suggestions,
  onSend,
  onSuggestionClick,
  disabled = false,
  onFeedbackSubmit,
  feedbackConfirmations,
  conversationId,
  userId,
  onMenuTrigger,
  streamState = { event: 'start', loading: false },
  isBallotOpen,
  userName,
  isReturningUser,
  depth,
  onDepthChange,
}) {
  const { t } = useTranslation('chat');
  const hasUserMessages = messages.some((m) => m.sender === 'user');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Wraps the bottom-pinned ChatInput so the BallotSheet can anchor
  // its teal header on top of the input (via
  // ``BallotSheetProvider.setChatInputRect``).
  const chatInputWrapperRef = useRef<HTMLDivElement>(null);
  // Sticky bottom (suggestions + grabber + input); subtracted from the reserve.
  const stickyBottomRef = useRef<HTMLDivElement>(null);
  const [stickyBottomH, setStickyBottomH] = useState(0);
  const [containerBottomY, setContainerBottomY] = useState(0);
  // At-bottom snapshot (real scrolls only) so the pop knows if the user was there.
  const wasAtBottomRef = useRef(true);

  const {
    setChatInputRect,
    keyboardUp,
    setPreviewClosed,
    snapTo,
    setActiveTab,
    closedDetentFrac,
    isDragging: sheetIsDragging,
    restingFrac,
    restingSheetTopPx,
    maxHeightPx,
  } = useBallotSheet();
  const navMode = useNavMode();

  // While the user is dragging the BallotSheet, freeze the chat
  // scroll container so a vertical gesture that started on the sheet
  // doesn't simultaneously scroll the chat content (the chat scroll
  // is a sibling subtree, but on mobile a finger-down on the sheet
  // header followed by an aggressive vertical drag can sometimes
  // trigger the chat scroll's momentum as well — they're independent
  // scrolls, just not simultaneous). ``overflow: hidden`` for the
  // drag's duration is enough — the browser refuses to scroll the
  // element while overflow is non-auto/scroll. Restored on release.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;
    if (!sheetIsDragging) return undefined;
    const previousOverflow = container.style.overflow;
    container.style.overflow = 'hidden';
    return () => {
      container.style.overflow = previousOverflow;
    };
  }, [sheetIsDragging]);
  const { user } = useAuth();
  const { address: guestAddress } = useGuestAddress();
  const {
    records: ballotRecords,
    recordsLoading: ballotRecordsLoading,
    building: ballotBuilding,
  } = useBallotRecords();

  // Surface the ``MobileAddressBar`` (gated on ``closedDetentFrac > 0``, the
  // previewClosed state) whenever there's no ballot to show:
  //   - a guest with no saved address, or
  //   - a signed-in user who deleted their last ballot (no records left).
  // This is the mobile equivalent of the desktop landing's address entry.
  // Collapses back once an address is entered / a ballot is (re)built.
  useEffect(() => {
    const isGuest = !user;
    const hasNoAddress = !guestAddress?.formatted_address;
    const authedNoBallot =
      !isGuest &&
      Boolean(user?.initialSetupComplete) &&
      !ballotRecordsLoading &&
      !ballotBuilding &&
      ballotRecords.length === 0;
    setPreviewClosed((isGuest && hasNoAddress) || authedNoBallot);
  }, [user, guestAddress, ballotRecords, ballotRecordsLoading, ballotBuilding, setPreviewClosed]);

  // Whether the ``MobileAddressBar`` is currently mounted (mirrors the
  // condition above). Used to drop the in-chat BallotGrabber and to
  // pad the sticky-bottom container so the chat input sits above the
  // bar instead of being overlapped by it.
  // In tab-bar mode the sheet and its address bar aren't rendered, so this stays
  // false there regardless of the sheet's preview state.
  const mobileAddressBarActive = navMode === 'sheet' && closedDetentFrac > 0;

  // Sheet/grabber UI only exists on the mobile breakpoint. Used below
  // to gate ``flushTop`` on the chat input (top corners stay rounded
  // on desktop where there's nothing sitting directly above the input).
  const theme = useTheme();
  // ``noSsr`` so the first render already knows the breakpoint. The opening-tab
  // effect below runs once at mount and reads this; with the default
  // SSR-safe behavior it would see ``false`` on that first pass and only
  // correct itself afterwards, by which point the one-shot ref has been set —
  // landing a phone guest with no address on Chat instead of the Ballot tab.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  const LOGO_GAP_PX = 20;

  // Reserve at mid; while at full, freeze to the pre-full value so closing reveals chat.
  const atMid = isMobile && restingFrac > 0 && restingFrac < 1;
  const isFull = isMobile && restingFrac >= 1;
  const liveLift = atMid ? restingFrac * maxHeightPx : 0;
  const liveOverlap = atMid ? Math.max(0, containerBottomY - restingSheetTopPx) : 0;
  const liftRef = useRef(liveLift);
  const overlapRef = useRef(liveOverlap);
  if (!isFull) {
    liftRef.current = liveLift;
    overlapRef.current = liveOverlap;
  }
  const sheetLiftPx = isFull ? liftRef.current : liveLift;
  const sheetOverlapPx = isFull ? overlapRef.current : liveOverlap;
  const tailReservePx = LOGO_GAP_PX + Math.max(0, sheetOverlapPx - stickyBottomH);

  // Container bottom edge (stable; resize-tracked), paired with the sheet top.
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    const measure = () => setContainerBottomY(el.getBoundingClientRect().bottom);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [hasUserMessages]);

  // Keep the sticky-bottom height current (suggestions wrap, input grows).
  useEffect(() => {
    const el = stickyBottomRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setStickyBottomH(el.offsetHeight));
    ro.observe(el);
    setStickyBottomH(el.offsetHeight);
    return () => ro.disconnect();
  }, [hasUserMessages]);

  // Track at-bottom on real scrolls only (not reserve/content resizes).
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      wasAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 40;
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasUserMessages]);

  // Pop to bottom once when the sheet settles at mid, if the user was at bottom.
  // Trigger on the atMid transition; double rAF lets the reserve lay out first.
  const wasAtMidRef = useRef(atMid);
  useLayoutEffect(() => {
    const landedAtMid = atMid && !wasAtMidRef.current;
    wasAtMidRef.current = atMid;
    if (!landedAtMid || !hasUserMessages || !wasAtBottomRef.current) return undefined;
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [atMid, hasUserMessages]);

  // Decide the mobile opening view once per mount, from one-shot flags:
  // "See my ballot" opens the ballot (Ballot tab, or sheet at full); a guest
  // with no address and no prior chat opens the Ballot tab for address entry
  // unless they chose "skip"; otherwise the default Chat tab / closed sheet.
  const openingTabDecidedRef = useRef(false);
  useEffect(() => {
    if (openingTabDecidedRef.current || typeof sessionStorage === 'undefined') return;
    openingTabDecidedRef.current = true;
    if (sessionStorage.getItem(LANDING_OPEN_FULL_FLAG) === '1') {
      sessionStorage.removeItem(LANDING_OPEN_FULL_FLAG);
      // Tab mode opens the Ballot tab; sheet mode expands the sheet to full.
      if (navMode === 'tab') setActiveTab('ballot');
      else snapTo('full');
      return;
    }
    const skipped = sessionStorage.getItem(LANDING_SKIPPED_FLAG) === '1';
    if (skipped) sessionStorage.removeItem(LANDING_SKIPPED_FLAG);
    // Guest-with-no-address auto-open only applies in tab mode on mobile; in
    // sheet mode the MobileAddressBar already surfaces the address entry over
    // the closed sheet, so there's no tab to switch to.
    if (navMode !== 'tab' || !isMobile || skipped || user || guestAddress?.formatted_address)
      return;
    if (readGuestChat()?.messages.some((m) => m.sender === 'user')) return;
    setActiveTab('ballot');
    // Mount-only by design — the ref guard makes this idempotent if the effect
    // ever re-fires, and the inputs are read at mount on purpose. ``snapTo`` /
    // ``setActiveTab`` are stable callbacks from ``BallotSheetProvider``.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ``useLayoutEffect`` (not ``useEffect``) so ``chatInputRect`` is
  // set synchronously after commit but BEFORE paint — the first
  // paint of the active-chat state already has the sheet hidden and
  // the BallotGrabber visible, with no intermediate frame showing
  // the landing-position sheet.
  useLayoutEffect(() => {
    if (!hasUserMessages) {
      setChatInputRect(null);
      return undefined;
    }
    const el = chatInputWrapperRef.current;
    if (!el) return undefined;

    const measure = () => {
      const r = el.getBoundingClientRect();
      setChatInputRect({ top: r.top, left: r.left, width: r.width });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      setChatInputRect(null);
    };
    // ``keyboardUp`` is a dependency because keyboard open/close
    // shifts the input wrapper's POSITION (via the sticky-bottom's
    // ``var(--keyboard-inset-px)`` padding) without changing its
    // SIZE — so ResizeObserver alone misses it. Re-running the
    // effect when keyboard state flips calls ``measure()`` once and
    // pushes the new rect into ``BallotSheetProvider`` so the sheet
    // can re-anchor above the input's new position.
  }, [hasUserMessages, setChatInputRect, keyboardUp]);

  // Simple state to track if we've scrolled for this bot message
  const [hasScrolled, setHasScrolled] = useState(false);

  // State to track if user is at bottom
  const [isAtBottom, setIsAtBottom] = useState(false);

  const contentWidth = useMemo(
    () => ({ xs: '100vw', sm: isBallotOpen ? 'calc(50vw - 70px)' : 'calc(100vw - 70px)' }),
    [isBallotOpen]
  );

  const contentMaxWidth = useMemo(
    () => ({
      xs: MAX_WIDTH_XS,
      sm: isBallotOpen ? '100%' : MAX_WIDTH_SM,
      md: isBallotOpen ? '100%' : MAX_WIDTH_MD,
      lg: isBallotOpen ? '100%' : MAX_WIDTH_LG,
    }),
    [isBallotOpen]
  );

  // Scroll to bottom when messages are loaded (for chat history)
  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200); // Give time for messages to render
    }
  }, [messages.length]); // Run when messages are loaded

  // Scroll function - scroll once per bot message
  const scrollOnce = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHasScrolled(true);
    }
  }, []);

  // Scroll when bot message starts loading (only once)
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.sender === 'bot' && lastMessage.isLoading && !hasScrolled) {
      setTimeout(() => {
        scrollOnce();
      }, 100);
    }

    // Reset scroll flag when bot message finishes
    if (lastMessage && lastMessage.sender === 'bot' && !lastMessage.isLoading) {
      setHasScrolled(false);
    }
  }, [messages, scrollOnce, hasScrolled]);

  // Preserve scroll position on window resize by maintaining distance from bottom
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleResize = () => {
      const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight - container.clientHeight - distFromBottom;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show / hide the scroll-to-bottom button based on whether the
  // messages-end marker is currently visible ABOVE the chat input's
  // TOP edge.
  //
  // Previously this used an IntersectionObserver with a negative
  // ``rootMargin`` computed from the sticky-bottom box's height. That
  // box includes the ``padding-bottom`` that lifts the input above
  // the keyboard — so the rootMargin grew with the keyboard,
  // pushing the trigger threshold all the way down to the keyboard's
  // top edge (much lower than the visible chat input). It also had
  // a staleness problem: the rootMargin only updated when the
  // ResizeObserver callback fired and the IntersectionObserver was
  // re-created, lagging the actual layout by a frame or two during
  // keyboard transitions and producing inconsistent behavior.
  //
  // The new approach reads ``messagesEndRef.getBoundingClientRect()
  // .bottom`` and ``chatInputWrapperRef.getBoundingClientRect().top``
  // each time scroll or layout changes, and compares them directly.
  // No rootMargin, no staleness — the trigger is always exactly the
  // visible chat input's top.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;
    const measure = () => {
      const endEl = messagesEndRef.current;
      const inputEl = chatInputWrapperRef.current;
      if (!endEl || !inputEl) return;
      const endRect = endEl.getBoundingClientRect();
      const inputRect = inputEl.getBoundingClientRect();
      // ``endRect.bottom <= inputRect.top`` means the messages-end
      // marker is rendered ABOVE the chat input's top edge — i.e.,
      // the user is at the bottom of the conversation and nothing
      // is hidden behind the input.
      setIsAtBottom(endRect.bottom <= inputRect.top);
    };
    measure();
    container.addEventListener('scroll', measure, { passive: true });
    // ResizeObserver targets, each catching a different content
    // height change that can put messages behind the input:
    //   - chat input wrapper: keyboard open/close changes the
    //     sticky-bottom's pb and shifts the input up/down;
    //     suggestions/grabber toggles change the wrapper itself.
    //   - messagesContainerRef (messages list wrapper): grows when
    //     a bot message STREAMS in (length stays constant, but
    //     content height grows). Without observing this, the button
    //     wouldn't appear until the user manually scrolled.
    //   - scroll container: catches outer-container resizes.
    const inputEl = chatInputWrapperRef.current;
    const messagesEl = messagesContainerRef.current;
    const ro = new ResizeObserver(measure);
    if (inputEl) ro.observe(inputEl);
    if (messagesEl) ro.observe(messagesEl);
    ro.observe(container);
    return () => {
      container.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [messages.length]);

  return (
    <>
      {/* Menu trigger — always visible on mobile regardless of chat state */}
      {onMenuTrigger && (
        <Box sx={{ position: 'fixed', top: 80, left: 16, zIndex: 1300 }}>
          <IconButton
            onClick={onMenuTrigger}
            sx={{
              backgroundColor: semanticColors.overlayWhite90,
              '&:hover': {
                backgroundColor: primitiveColors.gray7,
              },
            }}
            aria-label={t('window.menuButton')}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {hasUserMessages ? (
        <>
          {/* Messages area */}
          <Box
            ref={scrollContainerRef}
            sx={{
              maxWidth: contentMaxWidth,
              mx: 'auto',
              background: primitiveColors.blue1,
              borderRadius: BORDER_RADIUS,
              px: { xs: 2, sm: isBallotOpen ? 2 : 4 },
              paddingTop: { xs: 7, sm: 0 },
              paddingBottom: 0,
              width: contentWidth,
              display: 'flex',
              flexDirection: 'column',
              // ``100%`` (not ``100dvh``/``100vh``) so the container
              // fills its parent panel — which is already correctly
              // sized to the available viewport by DashboardLayout.
              // Using a viewport unit here would overflow the parent
              // by the banner height, pushing the bottom edge off
              // screen and leaving a visible sliver above the home
              // indicator.
              height: '100%',
              overflow: 'auto',
              // ``contain`` keeps the iOS rubber-band stretch but
              // prevents it from chaining into the body. The
              // BallotGrabber lives inside this scroll container, so
              // the browser's compositor moves it with the rubber-
              // band along with the chat input — no JS measurement
              // needed.
              overscrollBehavior: 'contain',
              position: 'relative',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {/* Messages area - scrollable */}
            <Box
              flex={1}
              ref={messagesContainerRef}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                zIndex: 1,
                // Visual breathing room between the last message and
                // the sticky-bottom (suggestions / grabber / chat input).
                // Content-hidden-behind-input is now handled by the
                // sticky-bottom's own pb + the getBoundingClientRect-
                // based scroll-to-bottom button, so this is purely a
                // spacing knob. Instant (no transition) so the reserve is at full
                // height before the pop-scroll runs, else the scroll clamps.
                pb: `${tailReservePx}px`,
                pl: isBallotOpen ? { sm: 4 } : 0,
              }}
            >
              {messages.map((msg, index) => (
                <ChatBubble
                  key={msg.id}
                  message={msg}
                  onFeedbackSubmit={onFeedbackSubmit}
                  feedbackConfirmations={feedbackConfirmations}
                  conversationId={conversationId}
                  userId={userId}
                  streamState={streamState}
                  isLastMessage={index === messages.length - 1}
                />
              ))}
              <div ref={messagesEndRef} />
            </Box>

            {/* Sticky bottom area — suggestions, grabber, chat input */}
            <Box
              ref={stickyBottomRef}
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                // ``xs: 0`` so the input + grabber + AI disclaimer
                // sit at the same screen-edge offset as the chat
                // scroll container's own ``px: 2`` — matching the
                // new-chat / landing page's chat input width.
                px: { xs: 0, sm: 2 },
                // Bottom padding holds the input above whichever of
                // the keyboard / safe-area inset is currently in
                // effect, plus the MobileAddressBar when mounted.
                //
                // ``--keyboard-inset-px`` is the single source of
                // truth maintained by ``BallotSheetProvider`` —
                // vk.boundingRect on Chromium, visualViewport
                // fallback on iOS Safari, with the Edge Android
                // bottom-toolbar overshoot already subtracted at
                // the source. No per-browser handling here.
                //
                // ``env(safe-area-inset-bottom)`` covers the home
                // indicator / gesture-nav region when the keyboard
                // is down.
                //
                // ``var(--mobile-address-bar-height)`` is added
                // outside the max() since it's a sibling component
                // sitting between this input and the keyboard.
                pb:
                  navMode === 'tab'
                    ? 'calc(max(var(--keyboard-inset-px, 0px), env(safe-area-inset-bottom)) + var(--mobile-tab-bar-height, 0px))'
                    : 'calc(max(var(--keyboard-inset-px, 0px), env(safe-area-inset-bottom)) + var(--mobile-address-bar-height, 0px))',
                pl: isBallotOpen ? { xs: 0, sm: 6 } : { xs: 0, sm: 2 },
              }}
            >
              {/* Suggestions + grabber + input */}
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 2, sm: 3 },
                }}
              >
                <ChatSuggestions
                  suggestions={suggestions}
                  onSuggestionClick={onSuggestionClick}
                  loading={streamState.loading}
                />
                {/* Wraps the BallotGrabber so the scroll-to-bottom
                    button can be absolutely-positioned 5px above the
                    grabber's top edge, regardless of whether
                    suggestions are present. The relative anchor here
                    is the wrapper itself; the button uses
                    ``bottom: calc(100% + 5px)`` to land just above
                    its containing block. */}
                <Box sx={{ position: 'relative' }}>
                  <Box
                    // Faded out at the bottom of the conversation — keep the
                    // button out of the accessibility tree there too, so it is
                    // not announced or tabbed to while invisible.
                    aria-hidden={isAtBottom}
                    sx={{
                      position: 'absolute',
                      bottom: 'calc(100% + 5px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      opacity: isAtBottom ? 0 : 1,
                      transition: 'opacity 0.3s ease-in-out',
                      pointerEvents: isAtBottom ? 'none' : 'auto',
                      zIndex: 1,
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        if (messagesEndRef.current) {
                          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      tabIndex={isAtBottom ? -1 : 0}
                      sx={{
                        backgroundColor: semanticColors.overlayWhite90,
                        boxShadow: `0 2px 8px ${semanticColors.shadowLight}`,
                        '&:hover': {
                          backgroundColor: semanticColors.background,
                        },
                      }}
                      aria-label={t('window.scrollToBottom')}
                    >
                      <KeyboardArrowDownIcon />
                    </IconButton>
                  </Box>
                  {/* Static teal grabber that lives inside the chat
                      scroll container so the browser's compositor
                      rubber-bands it with the chat input during
                      overscroll. Crossfades with the real BallotSheet
                      (page-level, position: fixed) on drag — the
                      sheet takes over the moment heightFrac > 0.
                      Hidden when the MobileAddressBar is active so
                      there's no drag-up affordance in the
                      no-address scenario (the bar replaces both the
                      grabber and the sheet entirely). */}
                  {navMode === 'sheet' && !mobileAddressBarActive && <BallotGrabber />}
                </Box>
                {/* Wrapper exists solely so the BallotSheet can
                    measure the chat input's position and anchor its
                    teal header on top of it. See the
                    ``chatInputWrapperRef`` effect above.
                    ``flushTop`` squares the input's top corners so it
                    connects cleanly with the teal header above. */}
                <Box ref={chatInputWrapperRef}>
                  {/* ``flushTop`` squares the input's top corners so
                      it connects cleanly with the teal sheet/grabber
                      sitting directly on top of it.
                      - On desktop (md+): no sheet, no grabber — keep
                        the rounded top corners.
                      - On mobile with MobileAddressBar active: grabber
                        is hidden, the bar sits separately above —
                        keep the rounded corners too.
                      - On mobile otherwise: square the top so it
                        butts up cleanly against the grabber. */}
                  <ChatInput
                    onSend={onSend}
                    disabled={disabled}
                    // Square the top only in sheet mode, where the composer butts
                    // up under the grabber. In tab mode there's no grabber, so keep
                    // the rounded top corners.
                    flushTop={isMobile && navMode === 'sheet' && !mobileAddressBarActive}
                    depth={depth}
                    onDepthChange={onDepthChange}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        /* Landing state — shown when no user messages exist yet */
        <ChatLandingState
          onSend={onSend}
          onSuggestionClick={onSuggestionClick}
          disabled={disabled}
          isBallotOpen={isBallotOpen}
          userName={userName}
          isReturningUser={isReturningUser}
          depth={depth}
          onDepthChange={onDepthChange}
          sheetLiftPx={sheetLiftPx}
          sheetDragging={sheetIsDragging}
        />
      )}
    </>
  );
});
